import { Camera, ClipboardCheck, Layers3, Ruler, Wrench } from 'lucide-react';
import { PrimaryCTA } from '@/components/PrimaryCTA';

/**
 * Phase 8 refinement: editorial 5-step block.
 *
 * - Section background: warm ivory.
 * - Steps are number-led editorial blocks with hairline top borders, no
 *   card chrome, no shadows. Numbers rendered in display serif, champagne.
 * - Lucide icons OFF by default (`withIcons={false}`); pass true to restore
 *   the previous look on any page that wants it.
 *
 * Used on: Homepage, /impact-window-cost-miami, /no-sales-impact-windows,
 *          /impact-windows-miami.
 */

const STEPS = [
  {
    n: '01',
    icon: Camera,
    title: 'Add Your Openings',
    body: 'Upload photos of the windows and doors you want quoted.',
  },
  {
    n: '02',
    icon: ClipboardCheck,
    title: 'We Review Your Project',
    body:
      'Our team reviews your opening photos, project type, access, and product options.',
  },
  {
    n: '03',
    icon: Layers3,
    title: 'Get Good / Better / Premium Pricing',
    body: 'Compare options from brands like ECO, Mr. Glass, and ES Windows.',
  },
  {
    n: '04',
    icon: Ruler,
    title: 'Confirm Final Measurements',
    body:
      'Before anything is ordered, final field measurements are confirmed.',
  },
  {
    n: '05',
    icon: Wrench,
    title: 'Permit + Install',
    body:
      'We help guide the project through permitting, installation, inspections, and completion.',
  },
];

export default function HowItWorks5({
  src = 'how_it_works',
  withCTA = true,
  withIcons = false,
  variant = 'default', // 'default' | 'compact'
  className = '',
}) {
  const compact = variant === 'compact';
  return (
    <section
      className={`bg-[var(--snap-ivory)] ${
        compact ? 'py-16 sm:py-20' : 'py-20 sm:py-28'
      } ${className}`}
      data-testid="how-it-works-5"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-12 sm:mb-16">
          <p
            className="text-[11px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-[var(--snap-champagne-600)] mb-4"
          >
            How Snap Works
          </p>
          <h2
            className="font-heading text-[34px] sm:text-[44px] md:text-[52px] leading-[1.05] text-[var(--snap-charcoal)]"
            data-testid="how-it-works-headline"
          >
            From photos to permit to install — without an in-home appointment.
          </h2>
        </div>

        <ol
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-y-10 sm:gap-y-12 gap-x-8 sm:gap-x-10"
          data-testid="how-it-works-steps"
        >
          {STEPS.map(({ n, icon: Icon, title, body }) => (
            <li
              key={n}
              className="relative pt-6 border-t border-[var(--snap-stone-200)]"
              data-testid={`how-it-works-step-${parseInt(n, 10)}`}
            >
              <div className="flex items-baseline gap-3 mb-3">
                <span
                  className="font-heading text-[26px] sm:text-[28px] leading-none text-[var(--snap-champagne-600)]"
                  aria-hidden="true"
                >
                  {n}
                </span>
                {withIcons && (
                  <Icon
                    className="h-4 w-4 text-[var(--snap-slate-500)]"
                    strokeWidth={1.5}
                  />
                )}
              </div>
              <h3 className="font-heading text-[19px] sm:text-[20px] leading-snug text-[var(--snap-charcoal)] mb-2">
                {title}
              </h3>
              <p className="text-[14.5px] sm:text-[15px] text-[var(--snap-slate-500)] leading-relaxed">
                {body}
              </p>
            </li>
          ))}
        </ol>

        {withCTA && (
          <div className="mt-14 sm:mt-16 flex flex-col sm:flex-row items-center gap-4 justify-center">
            <PrimaryCTA
              src={src}
              testId="how-it-works-primary-cta"
              fullWidthMobile={false}
            />
            <p className="text-[13px] sm:text-sm italic text-[var(--snap-slate-500)] text-center sm:text-left">
              No in-home sales appointment. Replies within 24 hours.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
